"use strict";
/**
 * Supplier linking enums (GH547).
 *
 * Vocabulary for linking our catalog articles to what each supplier sells, for the
 * supplier's price lists and for every commercial condition a supplier gives.
 *
 * Replaces the GH518 enums (`PriceListSupplier`, `SupplierLinkStatus`, `SupplierMatchConfidence`),
 * which closed the door to anyone but Telvet and Difarvet. A supplier is now identified by its
 * QVET id everywhere; which suppliers have a portal is a property of the supplier, not an enum.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.HABITUAL_PROPOSAL_REASON_LABELS = exports.HabitualProposalReason = exports.HABITUAL_REVIEW_STATUS_LABELS = exports.HabitualReviewStatus = exports.SUPPLIER_LAB_RESOLUTION_LABELS = exports.SupplierLabResolution = exports.LINK_QUEUE_MISSING_REASON_LABELS = exports.LinkQueueMissingReason = exports.LINK_QUEUE_VIEW_LABELS = exports.LinkQueueView = exports.SUPPLIER_LINK_WAIVER_REASON_LABELS = exports.SupplierLinkWaiverReason = exports.CAMPAIGN_PROGRESS_LABELS = exports.CampaignProgress = exports.PROMOTION_STATUS_LABELS = exports.PromotionStatus = exports.PromotionExtraction = exports.PROMOTION_VALIDITY_KIND_LABELS = exports.PromotionValidityKind = exports.PROMOTION_PRICE_BASIS_LABELS = exports.PromotionPriceBasis = exports.PromotionFreeUnits = exports.PROMOTION_MECHANIC_TYPE_LABELS = exports.PromotionMechanicType = exports.PROMOTION_SCOPE_KIND_LABELS = exports.PromotionScopeKind = exports.SUPPLIER_MATCH_CONFIDENCE_LABELS = exports.SupplierMatchConfidence = exports.SUPPLIER_LINK_SOURCE_LABELS = exports.SupplierLinkSource = exports.SUPPLIER_PRODUCT_STATUS_LABELS = exports.SupplierProductStatus = exports.SupplierProductSourceKind = exports.SupplierPortalAdapter = exports.SUPPLIER_PRICE_SOURCE_KIND_LABELS = exports.SupplierPriceSourceKind = void 0;
/** Where a supplier's price list comes from. */
var SupplierPriceSourceKind;
(function (SupplierPriceSourceKind) {
    /** A public or authenticated web portal read by an adapter (Telvet, Difarvet, Desego). */
    SupplierPriceSourceKind["portal"] = "portal";
    /** Files the supplier sends: Excel, CSV, PDF or images (Temozón, Total Pets, EQP). */
    SupplierPriceSourceKind["file"] = "file";
    /** No price list at all (DH). */
    SupplierPriceSourceKind["none"] = "none";
})(SupplierPriceSourceKind || (exports.SupplierPriceSourceKind = SupplierPriceSourceKind = {}));
exports.SUPPLIER_PRICE_SOURCE_KIND_LABELS = {
    [SupplierPriceSourceKind.portal]: "Portal en línea",
    [SupplierPriceSourceKind.file]: "Archivo (Excel, PDF o imagen)",
    [SupplierPriceSourceKind.none]: "Sin lista",
};
/**
 * Backend adapters able to read a supplier portal. Adding a portal = one value here plus an
 * adapter in the backend. Which supplier uses which adapter is stored on the supplier.
 */
var SupplierPortalAdapter;
(function (SupplierPortalAdapter) {
    SupplierPortalAdapter["telvet"] = "telvet";
    SupplierPortalAdapter["difarvet"] = "difarvet";
})(SupplierPortalAdapter || (exports.SupplierPortalAdapter = SupplierPortalAdapter = {}));
/** Format of one imported price-list row's origin. */
var SupplierProductSourceKind;
(function (SupplierProductSourceKind) {
    SupplierProductSourceKind["portal"] = "portal";
    SupplierProductSourceKind["excel"] = "excel";
    SupplierProductSourceKind["csv"] = "csv";
    SupplierProductSourceKind["pdf"] = "pdf";
    SupplierProductSourceKind["image"] = "image";
    /**
     * Built from the supplier's electronic invoices (CFDI): code, name and last price of what we bought.
     * For suppliers whose price list has no codes (Total Pets) — the invoices carry them.
     */
    SupplierProductSourceKind["invoice"] = "invoice";
    SupplierProductSourceKind["manual"] = "manual";
})(SupplierProductSourceKind || (exports.SupplierProductSourceKind = SupplierProductSourceKind = {}));
/**
 * Review state of an imported price-list row.
 *
 * Portal rows are trusted and land `published`. Rows extracted from a file land `staging` and
 * only count once a person publishes them: nothing extracted by AI reaches a cost without review.
 */
var SupplierProductStatus;
(function (SupplierProductStatus) {
    SupplierProductStatus["staging"] = "staging";
    SupplierProductStatus["published"] = "published";
})(SupplierProductStatus || (exports.SupplierProductStatus = SupplierProductStatus = {}));
exports.SUPPLIER_PRODUCT_STATUS_LABELS = {
    [SupplierProductStatus.staging]: "Por revisar",
    [SupplierProductStatus.published]: "Publicado",
};
/** How we know an article ↔ supplier-product link is right. Ordered from strongest. */
var SupplierLinkSource;
(function (SupplierLinkSource) {
    /** The supplier's code on an electronic invoice (CFDI `NoIdentificacion`) matched our purchase line. */
    SupplierLinkSource["cfdi"] = "cfdi";
    /** QVET already held this supplier reference for the article. */
    SupplierLinkSource["qvet_reference"] = "qvet_reference";
    /** A person accepted a suggestion from the matching engine. */
    SupplierLinkSource["suggestion_accepted"] = "suggestion_accepted";
    /** A person searched and chose it by hand. */
    SupplierLinkSource["manual"] = "manual";
})(SupplierLinkSource || (exports.SupplierLinkSource = SupplierLinkSource = {}));
exports.SUPPLIER_LINK_SOURCE_LABELS = {
    [SupplierLinkSource.cfdi]: "Factura electrónica",
    [SupplierLinkSource.qvet_reference]: "Referencia de QVET",
    [SupplierLinkSource.suggestion_accepted]: "Sugerencia aceptada",
    [SupplierLinkSource.manual]: "Manual",
};
/** How much the suggestion engine trusts a candidate. Never used to link on its own. */
var SupplierMatchConfidence;
(function (SupplierMatchConfidence) {
    SupplierMatchConfidence["high"] = "high";
    SupplierMatchConfidence["review"] = "review";
    SupplierMatchConfidence["low"] = "low";
})(SupplierMatchConfidence || (exports.SupplierMatchConfidence = SupplierMatchConfidence = {}));
exports.SUPPLIER_MATCH_CONFIDENCE_LABELS = {
    [SupplierMatchConfidence.high]: "Alta",
    [SupplierMatchConfidence.review]: "Revisar",
    [SupplierMatchConfidence.low]: "Baja",
};
/**
 * What a promotion is attached to.
 *
 * The default is `items` — the supplier's own codes — because the invoices show different
 * mechanics per article inside one laboratory (Telvet · Holland: 1+1, 2+1 and 5+1). `lab_line` is a
 * shortcut for when the evidence covers the whole line (Telvet · Santgar 5+1). `invoice` has no
 * product: it is a spend campaign.
 */
var PromotionScopeKind;
(function (PromotionScopeKind) {
    PromotionScopeKind["items"] = "items";
    PromotionScopeKind["lab_line"] = "lab_line";
    PromotionScopeKind["invoice"] = "invoice";
})(PromotionScopeKind || (exports.PromotionScopeKind = PromotionScopeKind = {}));
exports.PROMOTION_SCOPE_KIND_LABELS = {
    [PromotionScopeKind.items]: "Artículos concretos",
    [PromotionScopeKind.lab_line]: "Toda la línea del laboratorio",
    [PromotionScopeKind.invoice]: "La factura o el acumulado",
};
/** The mechanic of a promotion. */
var PromotionMechanicType;
(function (PromotionMechanicType) {
    /** Buy N, get M free (5+1, 10+2, 2×1). */
    PromotionMechanicType["bonus"] = "bonus";
    /** A percentage off the price basis. */
    PromotionMechanicType["percent"] = "percent";
    /** A printed final price per product (DVGT lists). */
    PromotionMechanicType["fixed_price"] = "fixed_price";
    /** Explicitly no promotion — "this supplier does NOT bonus this line". Absence is data. */
    PromotionMechanicType["none"] = "none";
    /** Spend a goal inside a window, get a gift (Telvet campaigns). Never enters unit cost. */
    PromotionMechanicType["spend_goal"] = "spend_goal";
})(PromotionMechanicType || (exports.PromotionMechanicType = PromotionMechanicType = {}));
exports.PROMOTION_MECHANIC_TYPE_LABELS = {
    [PromotionMechanicType.bonus]: "Bonificación (N+M)",
    [PromotionMechanicType.percent]: "Descuento %",
    [PromotionMechanicType.fixed_price]: "Precio fijo",
    [PromotionMechanicType.none]: "Sin promoción",
    [PromotionMechanicType.spend_goal]: "Campaña por monto",
};
/** Which units are free in a mixed bonus. «Se bonifican las de menor valor» = `cheapest`. */
var PromotionFreeUnits;
(function (PromotionFreeUnits) {
    PromotionFreeUnits["same"] = "same";
    PromotionFreeUnits["cheapest"] = "cheapest";
})(PromotionFreeUnits || (exports.PromotionFreeUnits = PromotionFreeUnits = {}));
/** Which price the promotion is computed on. Mandatory: 3+3 on public ≠ 3+3 on vet (Holland). */
var PromotionPriceBasis;
(function (PromotionPriceBasis) {
    PromotionPriceBasis["vet"] = "vet";
    PromotionPriceBasis["public"] = "public";
})(PromotionPriceBasis || (exports.PromotionPriceBasis = PromotionPriceBasis = {}));
exports.PROMOTION_PRICE_BASIS_LABELS = {
    [PromotionPriceBasis.vet]: "Precio veterinario",
    [PromotionPriceBasis.public]: "Precio público",
};
var PromotionValidityKind;
(function (PromotionValidityKind) {
    PromotionValidityKind["permanent"] = "permanent";
    PromotionValidityKind["temporary"] = "temporary";
})(PromotionValidityKind || (exports.PromotionValidityKind = PromotionValidityKind = {}));
exports.PROMOTION_VALIDITY_KIND_LABELS = {
    [PromotionValidityKind.permanent]: "Permanente",
    [PromotionValidityKind.temporary]: "Temporal",
};
/** Where a promotion was read from. */
var PromotionExtraction;
(function (PromotionExtraction) {
    PromotionExtraction["manual"] = "manual";
    PromotionExtraction["ai"] = "ai";
    PromotionExtraction["invoice_evidence"] = "invoice_evidence";
})(PromotionExtraction || (exports.PromotionExtraction = PromotionExtraction = {}));
/** Lifecycle of a promotion. Only `active` enters a cost. */
var PromotionStatus;
(function (PromotionStatus) {
    PromotionStatus["draft"] = "draft";
    PromotionStatus["active"] = "active";
    PromotionStatus["expired"] = "expired";
    PromotionStatus["rejected"] = "rejected";
})(PromotionStatus || (exports.PromotionStatus = PromotionStatus = {}));
exports.PROMOTION_STATUS_LABELS = {
    [PromotionStatus.draft]: "Borrador",
    [PromotionStatus.active]: "Activa",
    [PromotionStatus.expired]: "Vencida",
    [PromotionStatus.rejected]: "Descartada",
};
/** Progress of a spend campaign. */
var CampaignProgress;
(function (CampaignProgress) {
    CampaignProgress["open"] = "open";
    CampaignProgress["covered_unpaid"] = "covered_unpaid";
    CampaignProgress["delivered"] = "delivered";
    CampaignProgress["expired"] = "expired";
    CampaignProgress["in_dispute"] = "in_dispute";
})(CampaignProgress || (exports.CampaignProgress = CampaignProgress = {}));
exports.CAMPAIGN_PROGRESS_LABELS = {
    [CampaignProgress.open]: "Abierta",
    [CampaignProgress.covered_unpaid]: "Cubierta, falta pagar",
    [CampaignProgress.delivered]: "Entregada",
    [CampaignProgress.expired]: "Vencida",
    [CampaignProgress.in_dispute]: "Por revisar con el proveedor",
};
/** Why an article is NOT linked to a supplier it was bought from — takes it out of the queue. */
var SupplierLinkWaiverReason;
(function (SupplierLinkWaiverReason) {
    /** The supplier's list doesn't carry it (a special order, a product off their catalogue). */
    SupplierLinkWaiverReason["not_in_list"] = "not_in_list";
    /** Bought once or by exception; not worth linking. */
    SupplierLinkWaiverReason["one_off"] = "one_off";
    /** The QVET purchase line is the wrong article: fix the capture, not the link. */
    SupplierLinkWaiverReason["wrong_capture"] = "wrong_capture";
    SupplierLinkWaiverReason["other"] = "other";
})(SupplierLinkWaiverReason || (exports.SupplierLinkWaiverReason = SupplierLinkWaiverReason = {}));
exports.SUPPLIER_LINK_WAIVER_REASON_LABELS = {
    [SupplierLinkWaiverReason.not_in_list]: "No está en su lista",
    [SupplierLinkWaiverReason.one_off]: "Compra única o excepcional",
    [SupplierLinkWaiverReason.wrong_capture]: "Error de captura en QVET",
    [SupplierLinkWaiverReason.other]: "Otro motivo",
};
// ─── Queue views and laboratories (Tasks 19 a, 20) ───────────────────────────
/**
 * The two views of the «Pendientes» queue (Javier, 2026-09-19).
 *
 * - `no_links` (Sin ningún vínculo): bought in 12 months and linked to no supplier at all.
 * - `partial` (Vínculo parcial): linked to some supplier, still missing others.
 */
var LinkQueueView;
(function (LinkQueueView) {
    LinkQueueView["no_links"] = "no_links";
    LinkQueueView["partial"] = "partial";
})(LinkQueueView || (exports.LinkQueueView = LinkQueueView = {}));
exports.LINK_QUEUE_VIEW_LABELS = {
    [LinkQueueView.no_links]: "Sin ningún vínculo",
    [LinkQueueView.partial]: "Vínculo parcial",
};
/**
 * Why an article is expected to have a link to a supplier it lacks.
 *
 * - `bought` (Se le compró): bought from it in the last 12 months.
 * - `sells_brand` (Vende el laboratorio): a ranked supplier whose list carries the article's laboratory.
 */
var LinkQueueMissingReason;
(function (LinkQueueMissingReason) {
    LinkQueueMissingReason["bought"] = "bought";
    LinkQueueMissingReason["sells_brand"] = "sells_brand";
})(LinkQueueMissingReason || (exports.LinkQueueMissingReason = LinkQueueMissingReason = {}));
exports.LINK_QUEUE_MISSING_REASON_LABELS = {
    [LinkQueueMissingReason.bought]: "Se le compró",
    [LinkQueueMissingReason.sells_brand]: "Vende el laboratorio",
};
/**
 * How a list laboratory got its QVET brand.
 *
 * - `name` (Por nombre): its name matches a QVET brand by itself.
 * - `alias` (Decidido): a person chose the brand.
 * - `none` (Sin marca en QVET): no brand, found or decided.
 */
var SupplierLabResolution;
(function (SupplierLabResolution) {
    SupplierLabResolution["name"] = "name";
    SupplierLabResolution["alias"] = "alias";
    SupplierLabResolution["none"] = "none";
})(SupplierLabResolution || (exports.SupplierLabResolution = SupplierLabResolution = {}));
exports.SUPPLIER_LAB_RESOLUTION_LABELS = {
    [SupplierLabResolution.name]: "Por nombre",
    [SupplierLabResolution.alias]: "Decidido",
    [SupplierLabResolution.none]: "Sin marca en QVET",
};
// ─── Habitual review (Javier, 2026-09-19) ────────────────────────────────────
/**
 * Where an article stands in the habitual-supplier review.
 *
 * - `change` (Cambia): QVET marks another supplier as habitual — or none — than the proposal.
 * - `matches` (Ya coincide): QVET already marks the proposed one.
 * - `no_price` (Sin precio): no linked supplier has a list price, so there is nothing to compare.
 */
var HabitualReviewStatus;
(function (HabitualReviewStatus) {
    HabitualReviewStatus["change"] = "change";
    HabitualReviewStatus["matches"] = "matches";
    HabitualReviewStatus["no_price"] = "no_price";
})(HabitualReviewStatus || (exports.HabitualReviewStatus = HabitualReviewStatus = {}));
exports.HABITUAL_REVIEW_STATUS_LABELS = {
    [HabitualReviewStatus.change]: "Cambia",
    [HabitualReviewStatus.matches]: "Ya coincide",
    [HabitualReviewStatus.no_price]: "Sin precio",
};
/**
 * Why the proposal is that supplier.
 *
 * - `rank` (Primero en tu orden): first of the preference order; the cheapest doesn't beat it by the thresholds.
 * - `cost` (Más barato): cheaper with permanent conditions by at least $150 and 2 % a year.
 * - `manual` (Elegido a mano): a person chose it over the rule.
 */
var HabitualProposalReason;
(function (HabitualProposalReason) {
    HabitualProposalReason["rank"] = "rank";
    HabitualProposalReason["cost"] = "cost";
    HabitualProposalReason["manual"] = "manual";
})(HabitualProposalReason || (exports.HabitualProposalReason = HabitualProposalReason = {}));
exports.HABITUAL_PROPOSAL_REASON_LABELS = {
    [HabitualProposalReason.rank]: "Primero en tu orden",
    [HabitualProposalReason.cost]: "Más barato",
    [HabitualProposalReason.manual]: "Elegido a mano",
};
